import { AI_INSIGHTS_ENTITLEMENT_KEY } from "@/pages/AdminV2/Settings/aiInsightsAccessPageHelpers";

/**
 * Readiness of a feature for admin-driven rollout.
 *
 * - `available` — shown in the console; admins can roll it out.
 * - `in_development` — hidden for now. The feature owner has not marked it ready
 *   for admin to toggle. Kept in the registry so re-enabling is a one-line change
 *   (and so a future dev-staff readiness toggle can surface it).
 */
export type FeatureAccessStatus = "available" | "in_development";

/**
 * One feature managed by the admin Feature flags console.
 *
 * @remarks
 * `entitlementKey` is the key stored in the `entitlements` table and read by the
 * runtime gate for the feature. The console resolves access the same way the
 * gate does: an org-scoped row overrides the global row, and a missing row means
 * the feature is off (fail-closed).
 */
export interface FeatureAccessDefinition {
  entitlementKey: string;
  label: string;
  description: string;
  status: FeatureAccessStatus;
}

/**
 * Features the admin Feature flags console knows about.
 *
 * @remarks
 * To manage a new feature, add a row here with the entitlement key its runtime
 * gate reads and a `status`. Keep the key in sync with the backend resolver.
 *
 * Full workflow (for "add a feature to the feature flag table" requests):
 * `docs/admin/readmes/FEATURE_FLAGS_CONSOLE.md` → "Adding a feature to the console".
 *
 * @see services/api/src/service/organisationService.ts (scenario_calculator_v2 gate)
 */
export const FEATURE_ACCESS_REGISTRY: FeatureAccessDefinition[] = [
  {
    // Mirrors SCENARIO_CALCULATOR_V2_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts.
    entitlementKey: "scenario_calculator_v2",
    label: "Scenario Calculator v2",
    description:
      "The rebuilt Scenario Calculator. When off, customers stay on the original version.",
    status: "available",
  },
  {
    // Mirrors SCENARIO_CALCULATOR_CAROUSEL_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts.
    entitlementKey: "scen-calc-carousel-flag",
    label: "Scen-Calc-carousel-flag",
    description:
      "The first-use guided tour carousel on the Scenario Calculator. When off, customers see no tour and no Guided tour menu entry.",
    status: "available",
  },
  {
    // Mirrors BUDGET_EDITOR_V2_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts.
    entitlementKey: "budget_editor_v2",
    label: "Budget Editor v2",
    description:
      "The redesigned Budget Editor. When off, customers stay on the legacy version.",
    status: "available",
  },
  {
    // Mirrors INCIDENT_BANNER_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts. Operational switch,
    // not a rollout: turn on for all organisations while an incident is
    // active, off again once resolved. Customers see the change on their
    // next page load.
    entitlementKey: "incident_banner",
    label: "Incident banner",
    description:
      "Site-wide incident notice at the top of every page in the customer app. Turn on during an incident; turn off once resolved.",
    status: "available",
  },
  {
    // Mirrors NEW_CUSTOMER_PRICING_V2_ENTITLEMENT_KEY in
    // services/api/src/service/newCustomerPricing.ts. Billing switch, not a
    // feature rollout: turn the global row on at go-live so new signups are
    // charged the new product's prices. Kept `in_development` so only dev
    // staff (featureflags.manage) see it by default. Rollout controls require
    // temporary readiness; restore not-ready after the approved operation.
    // Readiness does not undo pricing. API writes still require dev staff
    // (adminV2DevStaffOnlyEntitlements), including while the row is ready.
    // Runbook: docs/runbooks/NEW_CUSTOMER_PRICING_V2.md.
    entitlementKey: "new_customer_pricing_v2",
    label: "New customer pricing",
    description:
      "Charges new signups the new pricing ($49 a month or $450 a year, 14 day trial). Existing subscribers keep their current price.",
    status: "in_development",
  },
  {
    // Mirrors REPORT_PERIOD_PICKER_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts. In development until
    // the period picker has passed staff testing; dev staff can mark it ready.
    entitlementKey: "report_period_picker",
    label: "Report period picker",
    description:
      "The reports header picker for months, quarters, financial years and custom month ranges. When off, customers keep the single-month selector.",
    status: "in_development",
  },
  {
    // Mirrors BUDGET_EDITOR_GROUPINGS_ENTITLEMENT_KEY in
    // services/api/src/service/organisationService.ts. Off at go live; dev
    // staff mark it ready once groupings are approved for customers.
    entitlementKey: "budget_editor_groupings",
    label: "Budget Editor groupings",
    description: "Lets owners group accounts in the Budget Editor.",
    status: "in_development",
  },
  {
    // Hidden for now: AI insights is not yet ready for admin to toggle here.
    // The AI insights access page (Admin → Product → AI insights access)
    // remains the place to manage it until its owner marks it ready.
    entitlementKey: AI_INSIGHTS_ENTITLEMENT_KEY,
    label: "AI insights",
    description:
      "AI-generated insights across reports. Shares the same entitlement as the AI insights access page.",
    status: "in_development",
  },
];

/** Every entitlement key the console manages (for the readiness lookup). */
export const FEATURE_ACCESS_ENTITLEMENT_KEYS = FEATURE_ACCESS_REGISTRY.map(
  (feature) => feature.entitlementKey,
);

/**
 * Whether a feature is ready to show to normal admins.
 *
 * @remarks
 * Effective readiness = the dev-staff override (`readiness`) if one is stored,
 * otherwise the feature's static `status` default. The override lets dev staff
 * mark a feature ready (or not) without a deploy.
 */
export const isFeatureReady = (
  feature: FeatureAccessDefinition,
  readiness: Record<string, boolean>,
): boolean =>
  readiness[feature.entitlementKey] ?? feature.status === "available";

/**
 * Features the console should render.
 *
 * @remarks
 * Dev staff see every registered feature (so they can mark in-development ones
 * ready). Everyone else sees:
 * - every `available` feature, even when its readiness toggle has been flipped
 *   off — the row keeps it visible but blocks rollout behind the "not ready"
 *   modal (see
 *   `apps/admin/src/vnext/routes/settings/feature-flags/FeatureAccessRow.tsx`)
 *   instead of hiding it, so admins can see that it is not ready yet; and
 * - `in_development` features only once they have been marked ready, so a
 *   feature deliberately parked in the registry (e.g. one still managed from its
 *   own page) stays hidden until its owner opts in.
 */
export const resolveVisibleFeatures = (args: {
  readiness: Record<string, boolean>;
  isDevStaff: boolean;
}): FeatureAccessDefinition[] =>
  args.isDevStaff
    ? FEATURE_ACCESS_REGISTRY
    : FEATURE_ACCESS_REGISTRY.filter(
        (feature) =>
          feature.status === "available" ||
          isFeatureReady(feature, args.readiness),
      );
